import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useSession } from '@descope/react-sdk';
import { getLastViewedProfile, ProfileType } from '@/lib/profilePersistence';
import { useOrganizations } from '@/hooks/api/useOrganizations';

/**
 * Restores the last viewed profile (personal account or organization)
 * after user signs in. Only runs once when authentication state changes.
 */
export function ProfileRestoration() {
  const { isAuthenticated, isSessionLoading } = useSession();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { data: organizations, isLoading: orgsLoading } = useOrganizations();

  const lastViewedOrgProfile = useMemo(() => {
    const lastProfile = getLastViewedProfile();

    if (lastProfile?.profileType === ProfileType.ORGANIZATION) {
      const org = organizations?.find(org => org.id === lastProfile.profileId);
      if (org) return org;
    }
  }, [organizations]);

  const shouldRedirectToLastViewedOrganizationProfile = useMemo(() => {
    if (!isAuthenticated || isSessionLoading || orgsLoading) {
      return false;
    }
    // Don't redirect if user is already viewing a specific profile
    if (organizationId) {
      return false;
    }
    // Check if we're already on dashboard (to avoid redirect loops)
    if (window.location.pathname === '/dashboard') {
      return false;
    }

    return !!lastViewedOrgProfile;
  }, [
    isAuthenticated,
    isSessionLoading,
    orgsLoading,
    organizationId,
    lastViewedOrgProfile,
  ]);

  if (shouldRedirectToLastViewedOrganizationProfile && lastViewedOrgProfile) {
    return <Navigate to={`/${lastViewedOrgProfile.id}/dashboard`} replace />;
  }

  if (!orgsLoading) return <Navigate to="/dashboard" replace />;
  return null;
}
